// src/config/storyCanvas.js
//
// Fonte ÚNICA das dimensões do Story do Instagram e da zona útil de conteúdo.
// Vale para TODOS os pipelines de imagem compartilhável (treino e dieta,
// amostra e gerada) — nada de repetir 250/64/1080 em outro arquivo.
//
// ── Por que existe uma zona proibida ────────────────────────────────────────
// O Instagram cobre o topo do Story com a barra de progresso + @usuário e a
// base com a caixa de resposta/CTA. Qualquer texto, número, ícone, logo ou
// CTA nessas faixas some atrás da UI nativa. Fundo, gradiente e textura PODEM
// sangrar até a borda — só conteúdo informativo fica confinado.
//
//   y = 0 ……… 250   → proibido (topo)
//   y = 250 … 1670  → ZONA ÚTIL (1420 px)
//   y = 1670 … 1920 → proibido (base)
//   x = 0 … 64 e 1016 … 1080 → margem lateral mínima (corte em aspect ratios
//   diferentes de 9:16)
export const STORY_CANVAS = Object.freeze({
  WIDTH: 1080,
  HEIGHT: 1920,
  SAFE_TOP: 250,
  SAFE_BOTTOM: 250,
  SAFE_X: 64,
});

// O card compartilhável é RENDERIZADO em 270×480 (9:16 — tamanho de layout do
// ShareCard) e EXPORTADO em escala WIDTH/270 = 4 → PNG 1080×1920. As margens
// da zona útil são convertidas para o espaço do card pela MESMA razão, então
// o que se vê na amostra é exatamente o que sai no arquivo.
export const STORY_CARD = Object.freeze({ WIDTH: 270, HEIGHT: 480 });

export const STORY_CARD_SCALE = STORY_CARD.WIDTH / STORY_CANVAS.WIDTH; // 0.25

const toCardPx = (canvasPx) => canvasPx * STORY_CARD_SCALE;

// CSS custom properties injetadas no elemento do card. O SCSS posiciona os
// blocos informativos SÓ com `var(--story-safe-*)` — este objeto é a única
// definição numérica (62.5px / 62.5px / 16px no espaço do card).
export const storySafeAreaVars = () => ({
  '--story-safe-top': `${toCardPx(STORY_CANVAS.SAFE_TOP)}px`,
  '--story-safe-bottom': `${toCardPx(STORY_CANVAS.SAFE_BOTTOM)}px`,
  '--story-safe-x': `${toCardPx(STORY_CANVAS.SAFE_X)}px`,
});

// Escala do html2canvas para um nó de `nodeWidthPx` de largura sair com
// exatamente STORY_CANVAS.WIDTH px (270 → 4×; 1080 → 1×).
export const storyExportScale = (nodeWidthPx) =>
  STORY_CANVAS.WIDTH / (Number(nodeWidthPx) > 0 ? Number(nodeWidthPx) : STORY_CARD.WIDTH);

// ── FORMATO DE FEED (4:5) ───────────────────────────────────────────────────
//
// NÃO É "ESCALAR O STORY". O Story é 9:16 e as 17 artes têm ajustes de pixel
// arte por arte em `shareOverrides.js`, calibrados para o cartão 270×480.
// Trocar a proporção invalida todos esses ajustes — por isso o formato de feed
// tem os PRÓPRIOS patches, num mapa separado, e só as artes portadas de
// propósito aparecem nele.
//
// POR QUE 1080×1350 E NÃO 1080×1080: o feed do Instagram aceita 4:5 e é o
// formato que ocupa mais tela vertical no celular; o quadrado desperdiça
// altura. E a GRADE do perfil recorta em 3:4 — o que estiver nos ~7% de cima e
// de baixo some na miniatura. Por isso a zona útil do feed é menor
// proporcionalmente que a do story, mesmo sem barra de UI cobrindo nada.
export const FEED_CANVAS = Object.freeze({
  WIDTH: 1080,
  HEIGHT: 1350,
  // Reserva para o corte 3:4 da grade do perfil: ~7% em cima e embaixo.
  SAFE_TOP: 95,
  SAFE_BOTTOM: 95,
  SAFE_X: 64,
});

/** Cartão do feed: 270 de largura, 337,5 de altura (4:5), exportado 4×. */
export const FEED_CARD = Object.freeze({ WIDTH: 270, HEIGHT: 337.5 });

/** Os dois formatos, por chave. É o que o carrossel do share percorre. */
export const FORMATOS = Object.freeze({
  STORY: Object.freeze({
    chave: 'STORY',
    canvas: STORY_CANVAS,
    card: STORY_CARD,
  }),
  FEED: Object.freeze({
    chave: 'FEED',
    canvas: FEED_CANVAS,
    card: FEED_CARD,
  }),
});

/**
 * As variáveis de zona útil de QUALQUER formato.
 *
 * Generaliza `storySafeAreaVars`, que continua existindo porque é chamada em
 * vários lugares e mudar a assinatura dela quebraria os dois pipelines de uma
 * vez. Os nomes das variáveis CSS são os mesmos (`--story-safe-*`), então o
 * SCSS das artes não precisa saber em qual formato está sendo renderizado —
 * é o que permite portar uma arte mexendo só nos patches.
 */
export const safeAreaVarsDoFormato = (chave) => {
  const f = FORMATOS[chave] || FORMATOS.STORY;
  const razao = f.card.WIDTH / f.canvas.WIDTH;
  return {
    '--story-safe-top': `${f.canvas.SAFE_TOP * razao}px`,
    '--story-safe-bottom': `${f.canvas.SAFE_BOTTOM * razao}px`,
    '--story-safe-x': `${f.canvas.SAFE_X * razao}px`,
  };
};

/** Escala de exportação de um formato: 270 → 4× em ambos. */
export const exportScaleDoFormato = (chave, nodeWidthPx) => {
  const f = FORMATOS[chave] || FORMATOS.STORY;
  const largura = Number(nodeWidthPx) > 0 ? Number(nodeWidthPx) : f.card.WIDTH;
  return f.canvas.WIDTH / largura;
};
